import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Organization } from '../types/common'
import { toast } from 'sonner'
interface UsageQuota {
  current: number
  limit: number
  isExceeded: boolean
  percentageUsed: number
}
interface UsageTracking {
  knowledgeSpaces: UsageQuota
  chatbots: UsageQuota
  adminUsers: UsageQuota
  memberUsers: UsageQuota
  isLoading: boolean
  checkQuota: (type: keyof Organization['limits'], increment?: number) => boolean
  refreshUsage: () => Promise<void>
}
export const useUsageTracking = (): UsageTracking => {
  const { organization } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [quotas, setQuotas] = useState<Record<keyof Organization['limits'], UsageQuota>>({
    knowledgeSpaces: { current: 0, limit: 0, isExceeded: false, percentageUsed: 0 },
    chatbots: { current: 0, limit: 0, isExceeded: false, percentageUsed: 0 },
    adminUsers: { current: 0, limit: 0, isExceeded: false, percentageUsed: 0 },
    memberUsers: { current: 0, limit: 0, isExceeded: false, percentageUsed: 0 },
  })
  const calculateQuotas = (org: Organization | null) => {
    if (!org) return quotas
    const newQuotas = {} as Record<keyof Organization['limits'], UsageQuota>
    Object.keys(org.limits).forEach((key) => {
      const typedKey = key as keyof Organization['limits']
      const current = org.usage[typedKey]
      const limit = org.limits[typedKey]
      const percentageUsed = Math.round((current / limit) * 100)
      newQuotas[typedKey] = {
        current,
        limit,
        isExceeded: current >= limit,
        percentageUsed,
      }
      // Show warning when usage is above 90%
      if (percentageUsed >= 90) {
        toast.warning(`${typedKey} usage is at ${percentageUsed}%`, {
          id: `quota-warning-${typedKey}`,
          duration: 5000,
        })
      }
    })
    return newQuotas
  }
  useEffect(() => {
    if (organization) {
      setQuotas(calculateQuotas(organization))
    }
    setIsLoading(false)
  }, [organization])
  const checkQuota = (type: keyof Organization['limits'], increment: number = 1): boolean => {
    if (!organization) return false
    const quota = quotas[type]
    const wouldExceed = quota.current + increment > quota.limit
    if (wouldExceed) {
      toast.error(`${type} quota would be exceeded`, {
        description: `Limit of ${quota.limit} ${type} would be exceeded.`,
      })
      return false
    }
    return true
  }
  const refreshUsage = async () => {
    setIsLoading(true)
    try {
      // Here you would typically fetch fresh usage data from your backend
      // For now, we'll just recalculate based on current organization data
      if (organization) {
        setQuotas(calculateQuotas(organization))
      }
    } catch (error) {
      toast.error('Failed to refresh usage data')
    } finally {
      setIsLoading(false)
    }
  }
  return {
    ...quotas,
    isLoading,
    checkQuota,
    refreshUsage,
  }
}